import * as React from 'react';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import ArticleIcon from '@mui/icons-material/Article';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { useQuery } from '../hooks/useQuery';
import { useAuth } from '../hooks/useAuth';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Typography from '@mui/material/Typography';
import { CarregarNotaFiscalPage } from './CarregarNotaFiscal';
import { ImportarPDFPage } from './ImportarPDF';
import { SelecionarOrdemServicoPage } from './SelecionarOrdemServico';
import CircularProgress from '@mui/material/CircularProgress';
import { DemaisArquivosPage } from './DemaisArquivos';
import {
  useLocation,
  useParams,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import AlertaProtocoloVencido from '../components/AlertaProtocoloVencido';
import NotaFiscal from '../models/NotaFiscal';
import { decryptData, tryParse } from '../utils';

const steps = [
  'Carregar Nota Fiscal Eletrônica',
  'Selecionar Ordem de Serviço',
  'Importar PDF da Nota Fiscal',
  'Importar demais arquivos',
];

export const NovaNotaPage = () => {
  const { search } = useLocation();
  const { contratoId: contratoParams } = useParams();
  let dataParams = decryptData(contratoParams);
  dataParams = tryParse(dataParams);
  const contratoId = dataParams.contrato || '';
  const contratoDts = dataParams.contrato_dts || '';
  const cnpj = dataParams.cnpj || '';
  const isContract = dataParams.is_contract || '';
  const gestorTecnico = dataParams.gestor || '';
  const nota_id = dataParams.nota_id || '';

  const { enviarNota, obterNotaFiscalPorId } = useQuery();
  const { user } = useAuth();
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set());
  const [isLoading, setIsLoading] = React.useState(false);
  const [nfs, setNfs] = React.useState();
  const [notaFiscal, setNotaFiscal] = React.useState(new NotaFiscal());

  const navigate = useNavigate();

  React.useEffect(() => {
    if (!dataParams) {
      return navigate('/dashboard/contratos');
    }
  }, [dataParams]);

  React.useEffect(() => {
    buscarNotaFiscal();
  }, []);

  const buscarNotaFiscal = async () => {
    if (!nota_id) return;

    setIsLoading(true);
    const { data } = await obterNotaFiscalPorId(nota_id);
    notaFiscal.setFromParser(data.data);
    notaFiscal.tipo_envio_nf = data.data.tipo_envio_nf;
    notaFiscal.situacao_nf = data.data.situacao_nf;
    setNotaFiscal(notaFiscal);
    setIsLoading(false);
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const renderStepAtual = () => {
    switch (activeStep) {
      case 0:
        return (
          <CarregarNotaFiscalPage
            steps={steps}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            codigoContrato={contratoId}
            user={user}
            cnpj={cnpj}
            nfs={nfs}
            notaFiscal={notaFiscal}
            setNotaFiscal={setNotaFiscal}
            isContract={isContract}
            setNfs={setNfs}
            contratoId={contratoId}
            contratoDts={contratoDts}
            gestor={gestorTecnico}
          />
        );
      case 1:
        return (
          <SelecionarOrdemServicoPage
            steps={steps}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            contratoId={contratoId}
            nfs={notaFiscal}
            isContract={isContract}
            cnpj={cnpj}
            contratoDts={contratoDts}
            gestor={gestorTecnico}
          />
        );
      case 2:
        return (
          <ImportarPDFPage
            steps={steps}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            nfs={notaFiscal}
            isContract={isContract}
            cnpj={cnpj}
            contratoId={contratoId}
            contratoDts={contratoDts}
            gestor={gestorTecnico}
          />
        );
      case 3:
        return (
          <DemaisArquivosPage
            steps={steps}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            nfs={notaFiscal}
            isContract={isContract}
            cnpj={cnpj}
            contratoId={contratoId}
            contratoDts={contratoDts}
            gestor={gestorTecnico}
          />
        );
      default:
        break;
    }
  };

  return (
    <Box component="main" mt={8} sx={{ width: '100%' }}>
      <AppBar position="static" color={'primary'} style={{ padding: 10 }}>
        <Grid container spacing={0}>
          <Grid
            item
            xs={12.5}
            alignItems={'center'}
            display={'flex'}
            justifyContent="space-between"
          >
            <Grid item display="flex" alignItems="center">
              <IconButton
                onClick={() => {
                  if (activeStep === 0) {
                    navigate(`/dashboard/notas/${contratoParams}`);
                  } else {
                    setActiveStep(activeStep - 1);
                  }
                }}
                aria-label="Voltar"
              >
                <ArrowBackIcon
                  sx={{
                    fontSize: 30,
                    display: { xs: 'none', md: 'flex' },
                    color: '#fff',
                  }}
                  aria-hidden="true"
                />
              </IconButton>

              <ArticleIcon
                sx={{
                  fontSize: 40,
                  display: { xs: 'none', md: 'flex' },
                }}
                aria-hidden="true"
              />
              <Typography
                component="h1"
                variant="h5"
                sx={{ color: 'white', fontSize: 20 }}
              >
                Notas fiscais
              </Typography>
            </Grid>
            <Box component="div" sx={{ display: 'flex', gap: 2 }}>
              <Typography variant="body1" sx={{ color: 'white', fontSize: 16 }}>
                {isContract
                  ? `Contrato: ${contratoId}`
                  : `Pedido: ${contratoId}`}
              </Typography>

              {notaFiscal && notaFiscal.numero_nf ? (
                <Typography
                  variant="body1"
                  sx={{ color: 'white', fontSize: 16 }}
                >
                  {`Nota Fiscal: ${notaFiscal.numero_nf}`}
                </Typography>
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </AppBar>
      <AlertaProtocoloVencido />

      {isLoading ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}
          role="status"
          aria-live="polite"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box padding={5}>
          <Grid container spacing={10}>
            <Grid item sm={12} display={'flex'} justifyContent={'flex-end'}>
              <Box sx={{ width: '100%' }}>
                <Stepper
                  activeStep={activeStep}
                  aria-label="Etapas do envio de nota fiscal"
                >
                  {steps.map((label, index) => {
                    const stepProps = {};
                    const labelProps = {};
                    if (isStepSkipped(index)) {
                      stepProps.completed = false;
                    }
                    return (
                      <Step key={label} {...stepProps}>
                        <StepButton color="inherit" onClick={handleStep(index)}>
                          {label}
                        </StepButton>
                      </Step>
                    );
                  })}
                </Stepper>
                {activeStep === steps.length ? (
                  <React.Fragment>
                    <Typography sx={{ mt: 2, mb: 1 }}>
                      All steps completed - you&apos;re finished
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
                      <Box sx={{ flex: '1 1 auto' }} />
                      <Button onClick={handleReset}>Reset</Button>
                    </Box>
                  </React.Fragment>
                ) : (
                  renderStepAtual()
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};
